import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useBillingNouns, useSession } from '@/hooks/useSession';
import type { SetupState, SetupStepId } from '@/types';
import { buildSteps, firstUndone, type StepConfig } from './steps';

/** The step list for this person, in the team's nouns. Stable across renders. */
export function useSetupSteps(): StepConfig[] {
  const session = useSession();
  const nouns = useBillingNouns();
  return useMemo(
    () => buildSteps(nouns, session.isAdmin),
    [nouns.client, nouns.payer, session.isAdmin] // eslint-disable-line react-hooks/exhaustive-deps
  );
}

/**
 * The current step: the first not-done step unless the person picked another.
 * The pick resets whenever any step's done state changes, so finishing a step
 * hands over to the next undone one.
 */
export function useCurrentStep(steps: StepConfig[], state: SetupState | undefined) {
  const signature = state ? steps.map((s) => (s.isDone(state) ? '1' : '0')).join('') : '';
  const [picked, setPicked] = useState<SetupStepId | null>(null);
  const [pickedFor, setPickedFor] = useState(signature);

  useEffect(() => {
    if (pickedFor !== signature) {
      setPicked(null);
      setPickedFor(signature);
    }
  }, [signature, pickedFor]);

  const current = useMemo(() => {
    if (!state) return undefined;
    const chosen = picked ? steps.find((s) => s.id === picked) : undefined;
    return chosen ?? firstUndone(steps, state);
  }, [steps, state, picked]);

  const pick = useCallback((id: SetupStepId | null) => setPicked(id), []);

  return { current, pick };
}

const DESKTOP = '(min-width: 1024px)';

function subscribe(cb: () => void) {
  const mq = window.matchMedia(DESKTOP);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
}

/** True at Tailwind's `lg` and up, where the rail fits beside the page. */
export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(DESKTOP).matches,
    () => true
  );
}
